// #2203 — /client/accept-invite magic-link landing page.
//
// Consumes a token from the URL (?token=<hex>) and POSTs to
// /auth/client/accept-invite. Mirrors AcademicAcceptPage.tsx's problem shape
// (token-bearing magic link → backend POST → establish session → redirect),
// but NOT its auth gate: the stakeholder accept endpoint is public and mints
// a brand-new user + session itself (stakeholders.routes.ts's clientAuthRouter
// has no `authenticate` middleware), so there is no existing-session
// precondition to wait on here — unlike the academic flow, which requires the
// invitee to already be signed in.
//
// Session is adopted via setSession(token, user) — not setAuthFromToken(jwt) —
// because this endpoint's response already carries the full user object
// (stakeholders.routes.ts's accept handler returns { token, user, tenantId,
// projectId, role }), the same shape OAuthCallbackPage.tsx's callback response
// carries. setAuthFromToken exists for endpoints that return a bare token and
// require decoding the JWT payload to reconstruct identity (AcademicAcceptPage's
// case) — using it here would blank name/email for a first-time invitee, since
// the signed JWT payload only carries { userId, tenantId }.
//
// Dispatched by ApiError.status, NOT by message-substring matching — the exact
// class of fragility AcademicAcceptPage.tsx's own comments (#1784) document
// fixing once already: a stale-session 401 elsewhere in this codebase reads
// as "Invalid or expired token", which collides on substring with an
// invitation's own "expired" wording.
//
// This page never calls GET /client/my-project — that endpoint resolves an
// already-accepted stakeholder's identity and has no role in the acceptance
// step. Calling it here is exactly the pre-existing defect this page replaces
// (ClientOnboarding.tsx's "Accept Invitation" button did, cold, with no
// session, and got that same generic 401 back as if it were an invite error).
//
// #2220 — the invitee now sets their own password here instead of the
// backend silently generating and discarding a random one. Submission is
// gated on the form (password + confirmation), not auto-fired on mount —
// the accept POST only goes out once the invitee has entered a password
// that passes the same client-side check the backend re-validates
// (MIN_PASSWORD/MAX_PASSWORD below, mirroring ResetPasswordPage.tsx's
// constants and auth.ts's register schema — not invented here).
//
// R2 review note (2026-08-22): the submitted password is discarded, not
// applied, when the invited email already belongs to an existing account
// (stakeholders.routes.ts's existingUser branch — deliberately, so an
// invite link can never overwrite someone else's real credentials). This
// page can't know in advance which case it is (the accept call is the
// single atomic step that both consumes the token and resolves the user),
// so the form copy below is worded to not promise the submitted password
// will work for a future login in that case.

import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import PasswordInput from '../components/PasswordInput';

interface AcceptResponse {
  token: string;
  user: { id: string; email: string; name: string };
  tenantId: string;
  projectId: string;
  role: string;
}

// Mirrors the backend's acceptInviteBody Zod bounds (stakeholders.routes.ts)
// exactly — frontend validation is UX-only, the backend re-validates.
const MIN_PASSWORD = 8;
const MAX_PASSWORD = 72;

type State =
  | { kind: 'form'; error?: string }
  | { kind: 'submitting' }
  | { kind: 'invalid-or-used'; message: string }
  | { kind: 'server-error'; message: string };

export default function ClientAcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const { setSession } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState<State>({ kind: 'form' });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.kind !== 'form' || !token) return;

    if (password.length < MIN_PASSWORD || password.length > MAX_PASSWORD) {
      setState({ kind: 'form', error: `Password must be ${MIN_PASSWORD}-${MAX_PASSWORD} characters.` });
      return;
    }
    if (password !== confirmPassword) {
      setState({ kind: 'form', error: 'Passwords do not match.' });
      return;
    }

    setState({ kind: 'submitting' });
    try {
      const data = await apiFetch<AcceptResponse>('/auth/client/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      // Session must be adopted before navigating, so /client/dashboard
      // renders under the invitee's own session rather than briefly
      // unauthenticated.
      //
      // #2280 gap 1 — carry the accepted project's id forward. project_stakeholders
      // has no unique constraint on (userId, projectId); an invitee who already
      // holds ACTIVE access on other projects would otherwise land on the
      // dashboard via GET /client/my-project's arbitrary .limit(1) fallback,
      // not necessarily the project they just accepted an invite for.
      setSession(data.token, data.user);
      navigate(`/client/dashboard?projectId=${encodeURIComponent(data.projectId)}`, { replace: true });
    } catch (err) {
      // Structural discriminator, not message-substring matching: a Zod
      // rejection from validate() (stakeholders.routes.ts's acceptInviteBody)
      // carries a `details` array in the body; the CAS-consumed/expired-token
      // rejection does not. Both are 400s, so status alone can't tell them
      // apart here — unlike ResetPasswordPage's 410-vs-422 split, this
      // endpoint doesn't have a second status code free to spend on it.
      // Client-side validation above already blocks the common case; this
      // only matters if it's ever bypassed or drifts from the backend bounds.
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        Array.isArray((err.body as { details?: unknown } | undefined)?.details)
      ) {
        setState({ kind: 'form', error: 'That password isn’t acceptable. Try a different one.' });
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setState({ kind: 'invalid-or-used', message: err.message });
        return;
      }
      setState({
        kind: 'server-error',
        message: err instanceof Error ? err.message : 'Failed to accept invitation',
      });
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', padding: 24,
    background: 'var(--surface-alt)',
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 440, width: '100%', padding: 32,
    borderRadius: 16, background: 'var(--surface)',
    border: '1px solid var(--border)', textAlign: 'center',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
    border: '1px solid var(--border)', background: 'var(--surface-alt)',
    color: 'var(--text)', boxSizing: 'border-box',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'block', width: '100%', padding: '10px 28px', borderRadius: 8,
    border: 'none', cursor: 'pointer',
    background: 'var(--primary)', color: '#fff',
    fontWeight: 600, fontSize: 15,
  };

  // No token — defensive guard against a direct visit without ?token.
  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Missing invitation token</h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
            This page was opened without a valid invitation link.
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === 'invalid-or-used' || state.kind === 'server-error') {
    const heading = state.kind === 'invalid-or-used'
      ? 'This invitation is no longer valid'
      : 'Unable to accept invitation';
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--error, #dc2626)' }}>
            {heading}
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
            {state.message}
          </p>
        </div>
      </div>
    );
  }

  const submitting = state.kind === 'submitting';

  return (
    <div style={containerStyle}>
      <div style={{ ...cardStyle, textAlign: 'left' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, textAlign: 'center' }}>
          Set your password
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
          Choose a password for this project. If you already have a BluePrint account with this
          email, we&rsquo;ll keep using that account&rsquo;s existing password instead.
        </p>

        <form onSubmit={handleSubmit}>
          {state.kind === 'form' && state.error && (
            <div
              role="alert"
              data-testid="accept-invite-error"
              style={{ fontSize: 13, color: 'var(--error, #dc2626)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 16 }}
            >
              {state.error}
            </div>
          )}

          <label style={{ display: 'block', marginBottom: 16 }}>
            <span style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Password</span>
            <PasswordInput
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              maxLength={MAX_PASSWORD}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={submitting}
              style={inputStyle}
            />
          </label>

          <label style={{ display: 'block', marginBottom: 24 }}>
            <span style={{ fontSize: 13, fontWeight: 500, marginBottom: 4, display: 'block' }}>Confirm password</span>
            <PasswordInput
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              maxLength={MAX_PASSWORD}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={submitting}
              style={inputStyle}
            />
          </label>

          <button type="submit" disabled={submitting} style={{ ...buttonStyle, opacity: submitting ? 0.7 : 1 }}>
            {submitting ? 'Accepting…' : 'Accept invitation'}
          </button>
        </form>
      </div>
    </div>
  );
}
